import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RiMegaphoneLine } from '@remixicon/react'
import { useAuth } from '../../contexts/AuthContext'
import { useChangelogSeen } from '../../hooks/useChangelogSeen'
import { CHANGELOG } from '../../lib/changelog'
import { formatDate } from '../../lib/dates'
import { cx, focusRing } from '../../lib/utils'
import Badge from '../ui/Badge'
import Drawer from '../ui/Drawer'

const badgeVariant = { new: 'default', improved: 'neutral', fixed: 'success' } as const

/** The back-office's "what's new": a button with a dot while there is something unread, and the drawer it opens. */
export default function ChangelogButton() {
  const { t, i18n } = useTranslation()
  const { user } = useAuth()
  const { hasUnseen, markSeen } = useChangelogSeen(user?.id)
  const [open, setOpen] = useState(false)
  const lang = i18n.language.startsWith('en') ? 'en' : 'it'

  const openDrawer = () => {
    setOpen(true)
    markSeen()
  }

  return (
    <>
      <button
        type="button"
        onClick={openDrawer}
        aria-label={hasUnseen ? t('changelog.openUnseen') : t('changelog.title')}
        className={cx(
          'relative flex size-9 items-center justify-center rounded-md text-gray-500 transition hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-900 dark:hover:text-gray-50',
          focusRing,
        )}
      >
        <RiMegaphoneLine className="size-5" aria-hidden="true" />
        {hasUnseen && (
          <span
            data-testid="changelog-dot"
            className="absolute right-2 top-2 size-2 rounded-full bg-primary-500 ring-2 ring-white dark:ring-gray-950"
            aria-hidden="true"
          />
        )}
      </button>

      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        title={t('changelog.title')}
        subtitle={t('changelog.subtitle')}
        size="md"
      >
        <div className="space-y-8">
          {CHANGELOG.map((entry) => (
            <section key={entry.id} aria-labelledby={`changelog-${entry.id}`}>
              <h3
                id={`changelog-${entry.id}`}
                className="text-sm font-semibold text-gray-900 dark:text-gray-50"
              >
                {/* Noon, local: a bare date reads as UTC midnight, a day early west of Greenwich. */}
                {formatDate(`${entry.date}T12:00:00`, i18n.language)}
              </h3>
              <ul className="mt-3 space-y-3">
                {entry.items.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Badge variant={badgeVariant[item.type]} className="mt-0.5 shrink-0">
                      {t(`changelog.type_${item.type}`)}
                    </Badge>
                    <p className="text-sm text-gray-700 dark:text-gray-300">{item[lang]}</p>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </Drawer>
    </>
  )
}
